import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { Reasoning } from "./reasoning";
import { MarkdownRenderer } from "./markdown-renderer";
import { ToolCardRenderer } from "./tool-registry";
import type {
  AIElementsMessagePart,
  AIElementsReasoningPart,
  AIElementsToolPart,
  AIElementsTextPart,
  ToolCallData,
} from "../../adapters/chatAdapter";

const messageVariants = cva(
  "relative flex w-full gap-3 p-4",
  {
    variants: {
      role: {
        user: "flex-row-reverse",
        assistant: "flex-row",
        system: "flex-row justify-center",
      },
    },
    defaultVariants: {
      role: "assistant",
    },
  }
);

const messageContentVariants = cva(
  "relative max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed overflow-hidden",
  {
    variants: {
      role: {
        user: "bg-primary text-primary-foreground rounded-br-sm",
        assistant: "bg-muted text-foreground rounded-bl-sm border border-border/50",
        system: "bg-muted/50 text-muted-foreground text-xs px-3 py-1 rounded-full",
      },
    },
    defaultVariants: {
      role: "assistant",
    },
  }
);

export interface MessagePartRenderConfig {
  isStreaming?: boolean;
  streamingStartTime?: number;
}

export interface MessageProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "content">,
    VariantProps<typeof messageVariants> {
  role: "user" | "assistant" | "system";
  content?: React.ReactNode;
  avatar?: React.ReactNode;
  actions?: React.ReactNode;
  isLoading?: boolean;
  parts?: AIElementsMessagePart[];
  partConfig?: MessagePartRenderConfig;
}

function renderPart(
  part: AIElementsMessagePart,
  index: number,
  config?: MessagePartRenderConfig
): React.ReactNode {
  if (part.type === "text") {
    const textPart = part as AIElementsTextPart;
    return (
      <MarkdownRenderer
        key={`text-${index}`}
        content={textPart.content}
        className="text-sm leading-relaxed"
      />
    );
  }

  if (part.type === "reasoning") {
    const reasoningPart = part as AIElementsReasoningPart;
    return (
      <Reasoning
        key={`reasoning-${index}`}
        isStreaming={!reasoningPart.isDone}
        reasoningTime={reasoningPart.reasoningTime}
        streamingStartTime={config?.streamingStartTime}
        defaultOpen={!reasoningPart.isDone}
      >
        <div className="whitespace-pre-wrap">{reasoningPart.content || ""}</div>
      </Reasoning>
    );
  }

  if (part.type === "tool") {
    const toolPart = part as AIElementsToolPart;
    if (toolPart.toolName === 'ask_user') return null;
    const toolCallData: ToolCallData = {
      toolCallId: toolPart.toolCallId,
      toolName: toolPart.toolName,
      status: toolPart.status,
      args: toolPart.args,
      result: toolPart.result,
      error: toolPart.error,
      timestamp: toolPart.timestamp,
    };
    return (
      <ToolCardRenderer
        key={`tool-${index}`}
        toolCall={toolCallData}
      />
    );
  }

  return null;
}

const Message = React.forwardRef<HTMLDivElement, MessageProps>(
  (
    { className, role, content, avatar, actions, isLoading, parts, partConfig, children, ...props },
    ref
  ) => {
    const hasParts = parts && parts.length > 0;

    return (
      <div ref={ref} className={cn(messageVariants({ role }), className)} {...props}>
        {role !== "system" && (
          <div className="flex-shrink-0">
            {avatar || (
              <div
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium",
                  role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted border border-border/50 text-muted-foreground"
                )}
              >
                {role === "user" ? "U" : "AI"}
              </div>
            )}
          </div>
        )}

        <div className={cn("flex flex-col gap-1 min-w-0", role === "user" ? "items-end" : "items-start")}>
          <div className={cn(messageContentVariants({ role }))}>
            {isLoading ? (
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span>
                </span>
                <span className="text-xs opacity-70">思考中...</span>
              </div>
            ) : hasParts ? (
              <div className="space-y-2">
                {parts.map((part, index) => renderPart(part, index, partConfig))}
              </div>
            ) : (
              content || children
            )}
          </div>
          {actions && <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">{actions}</div>}
        </div>
      </div>
    );
  }
);
Message.displayName = "Message";

const MessageContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("space-y-2", className)} {...props}>
    {children}
  </div>
));
MessageContent.displayName = "MessageContent";

const MessageActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-1 mt-1", className)}
    {...props}
  >
    {children}
  </div>
));
MessageActions.displayName = "MessageActions";

const MessageAction = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      "inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
      className
    )}
    {...props}
  >
    {children}
  </button>
));
MessageAction.displayName = "MessageAction";

export { Message, MessageContent, MessageActions, MessageAction, messageVariants, messageContentVariants };
